import { Grid, Skeleton } from '@chakra-ui/react';
import { useResource, useResources } from 'datx-jsonapi-react';
import NextLink from 'next/link';
import { FC, Fragment } from 'react';

import { LectureIcon } from '@/components/ui/features/originals/RelatedLecturesSection/components/LectureIcon';
import { LectureCard } from '@/components/ui/shared/lecture/LectureCard/LectureCard';
import { LectureCardTag } from '@/components/ui/shared/lecture/LectureCard/LectureCardTags/LectureCardTags';
import { LectureCardImage } from '@/components/ui/shared/lecture/LectureCardImage/LectureCardImage';
import { useAnalytics } from '@/context/Analytics';
import { SlugIdParam, useQueryParams } from '@/hooks/useQueryParams';
import { getOriginalSeries } from '@/queries/originals-series';
import { queryOriginalsLectures } from '@/queries/thin-lectures';
import { LECTURE_CARD_IMAGE_FALLBACK } from '@/utils/constants';
import { dateToString } from '@/utils/date';

export const OriginalRelatedLectureFallback: FC = () => (
  <Fragment>
    <Skeleton h="250px" />
    <Skeleton h="250px" />
    <Skeleton h="250px" />
    <Skeleton h="250px" />
  </Fragment>
);

export const RelatedLectureDesktop: FC = () => {
  const analytics = useAnalytics();

  const { id: seriesId } = useQueryParams<{ id: string }>({
    id: SlugIdParam,
  });

  const { data: series } = useResource(getOriginalSeries(seriesId));
  const { data, error, mutate } = useResources(queryOriginalsLectures(series?.subject?.id));

  if (error) throw error;

  const isEmpty = data?.length === 0;

  return (
    <Grid display={{ base: 'none', xl: 'grid' }} templateColumns="repeat(auto-fill, minmax(250px, 1fr))" gap="8">
      {isEmpty && <OriginalRelatedLectureFallback />}
      {data?.map((lecture) => (
        <NextLink key={lecture.id} href={`/lecture/${lecture.linkSlug}`} passHref legacyBehavior>
          <LectureCard
            mutate={mutate}
            lecture={lecture}
            border="1px solid #CACCCC"
            onClick={() =>
              analytics.track('originals_related_lecture_click', {
                lecture_name: lecture.title,
                lecture_id: lecture.id,
                originals_id: seriesId,
              })
            }
            as="a"
            image={
              <LectureCardImage src={lecture.posterThumbnailUrl || LECTURE_CARD_IMAGE_FALLBACK} alt={lecture.title} />
            }
            tag={<LectureCardTag tagItem={lecture.tag} />}
            icon={<LectureIcon lecturePackage={lecture.package} />}
            authors={lecture.sortedProfiles}
            name={lecture.title}
            heldAt={dateToString(lecture.heldAt, 'dd MMMM yyyy')}
            eventName={lecture?.event?.name}
            awards={lecture?.awards}
          />
        </NextLink>
      ))}
    </Grid>
  );
};
